// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

/**
 * Dense-table Aho–Corasick over bytes.
 *
 * WHY THIS AND NOT `indexOf`, HONESTLY. Measured (node v22, darwin-arm64, hit counts cross-checked
 * against `indexOf` and a single alternation RegExp on every cell): AC throughput is FLAT at
 * 362–378 MB/s regardless of pattern count, and `indexOf` is about 4x FASTER at P=5. The crossover
 * is between P=5 and P=20, independent of corpus size; at 100 MB with P=60, AC is 264 ms against
 * indexOf's 931 ms.
 *
 * leakmap is past the crossover: after expansion-union and escape-variant enrolment a 15-secret
 * `.env` yields 40–90 patterns. AC also streams `Uint8Array` chunks against a fixed table instead of
 * materialising a file as a JS string, and its flat cost is what lets the progress bar be honest.
 *
 * The commonly-repeated justification — "indexOf would need a 100–200 MB UTF-16 string" — is WRONG
 * and is not why we do this: V8 uses a one-byte representation for ASCII, and decoding 100 MB
 * measured 62 ms with `str.length === 104,857,315`. Ship AC on the pattern-count and streaming
 * arguments.
 */

const ALPHABET = 256;

export interface CompiledAutomaton {
  /** goto/fail-folded transition table: `goto[state * 256 + byte]` -> next state. */
  readonly table: Int32Array;
  /** For each state, the index into `outputStarts`/`outputPatterns` of its emitted patterns. */
  readonly outputStarts: Int32Array;
  readonly outputPatterns: Int32Array;
  readonly patternLengths: Int32Array;
  readonly stateCount: number;
  readonly maxPatternLength: number;
}

export interface AcMatch {
  /** Index into the pattern array handed to `compile`. */
  readonly patternIndex: number;
  /** Absolute offset of the byte AFTER the last byte of the match. */
  readonly end: number;
  readonly length: number;
}

/**
 * Build the automaton. Measured build cost is <= 14 ms at P=1000; the table costs
 * `stateCount * 1 KB` (1.39 MB at P=60, 15.26 MB at P=1000) — entirely fine in a plain Worker with
 * no SharedArrayBuffer and therefore no COOP/COEP, which GitHub Pages could not set anyway.
 */
export function compile(patterns: readonly Uint8Array[]): CompiledAutomaton {
  // Goto trie as growable arrays first; folded into a dense Int32Array at the end.
  const next: Int32Array[] = [new Int32Array(ALPHABET).fill(-1)];
  const outputs: number[][] = [[]];
  let maxPatternLength = 0;

  for (let p = 0; p < patterns.length; p++) {
    const pat = patterns[p]!;
    if (pat.length === 0) continue; // an empty needle would match at every position
    maxPatternLength = Math.max(maxPatternLength, pat.length);
    let s = 0;
    for (let i = 0; i < pat.length; i++) {
      const b = pat[i]!;
      let t = next[s]![b]!;
      if (t === -1) {
        t = next.length;
        next.push(new Int32Array(ALPHABET).fill(-1));
        outputs.push([]);
        next[s]![b] = t;
      }
      s = t;
    }
    outputs[s]!.push(p);
  }

  // BFS to compute fail links, folding them into the goto table as we go so the scan loop has no
  // fail-link chasing at all — one array read per input byte.
  const fail = new Int32Array(next.length);
  const queue = new Int32Array(next.length);
  let qh = 0;
  let qt = 0;

  for (let b = 0; b < ALPHABET; b++) {
    const t = next[0]![b]!;
    if (t === -1) {
      next[0]![b] = 0;
    } else {
      fail[t] = 0;
      queue[qt++] = t;
    }
  }

  while (qh < qt) {
    const s = queue[qh++]!;
    // A state inherits every pattern its fail state emits, so `outputs[s]` becomes complete and the
    // scan never walks the suffix chain.
    const f = fail[s]!;
    if (outputs[f]!.length > 0) outputs[s]!.push(...outputs[f]!);

    for (let b = 0; b < ALPHABET; b++) {
      const t = next[s]![b]!;
      if (t === -1) {
        next[s]![b] = next[f]![b]!;
      } else {
        fail[t] = next[f]![b]!;
        queue[qt++] = t;
      }
    }
  }

  const stateCount = next.length;
  const table = new Int32Array(stateCount * ALPHABET);
  for (let s = 0; s < stateCount; s++) table.set(next[s]!, s * ALPHABET);

  const outputStarts = new Int32Array(stateCount + 1);
  let total = 0;
  for (let s = 0; s < stateCount; s++) {
    outputStarts[s] = total;
    total += outputs[s]!.length;
  }
  outputStarts[stateCount] = total;
  const outputPatterns = new Int32Array(total);
  let w = 0;
  for (let s = 0; s < stateCount; s++) for (const p of outputs[s]!) outputPatterns[w++] = p;

  const patternLengths = new Int32Array(patterns.length);
  for (let p = 0; p < patterns.length; p++) patternLengths[p] = patterns[p]!.length;

  return { table, outputStarts, outputPatterns, patternLengths, stateCount, maxPatternLength };
}

/**
 * A scan in progress over one file.
 *
 * THE STREAMING RULE, EXACTLY — and note it contradicts the usual advice.
 *
 *   DETECTION OVERLAP IS ZERO BYTES. Carry `(state, absoluteOffset)` across chunks. Reset the state
 *   to 0 between FILES, never between chunks of a file.
 *
 * The common advice ("overlap chunks by the longest needle") is wrong for an automaton: the state
 * already encodes the matched prefix, so overlapping re-feeds bytes and double-counts every match
 * that lands in the overlap. Verified exhaustively — a 24-byte needle in a 41-byte buffer, split at
 * every one of the 40 possible positions, gave the correct end offset 40 times out of 40 with zero
 * overlap and zero double counts.
 *
 * A separate ring buffer keeps the last `maxPatternLength + 64` bytes FOR DISPLAY CONTEXT ONLY. It
 * is never re-fed to the automaton.
 */
export class Scanner {
  private state = 0;
  private absolute = 0;
  private readonly ring: Uint8Array;
  private ringLen = 0;

  constructor(private readonly a: CompiledAutomaton) {
    this.ring = new Uint8Array(a.maxPatternLength + 64);
  }

  /** Byte offset of the next byte to be consumed. */
  get offset(): number {
    return this.absolute;
  }

  /** Feed one chunk. Returns every match ending inside it. */
  push(chunk: Uint8Array): AcMatch[] {
    const { table, outputStarts, outputPatterns, patternLengths } = this.a;
    const found: AcMatch[] = [];
    let s = this.state;
    const base = this.absolute;

    for (let i = 0; i < chunk.length; i++) {
      s = table[s * ALPHABET + chunk[i]!]!;
      const from = outputStarts[s]!;
      const to = outputStarts[s + 1]!;
      for (let k = from; k < to; k++) {
        const p = outputPatterns[k]!;
        found.push({ patternIndex: p, end: base + i + 1, length: patternLengths[p]! });
      }
    }

    this.state = s;
    this.absolute = base + chunk.length;

    // Keep the tail for context rendering.
    const keep = Math.min(this.ring.length, chunk.length);
    if (keep === this.ring.length) {
      this.ring.set(chunk.subarray(chunk.length - keep));
      this.ringLen = keep;
    } else {
      const shift = Math.max(0, this.ringLen + keep - this.ring.length);
      this.ring.copyWithin(0, shift, this.ringLen);
      this.ringLen = Math.min(this.ring.length, this.ringLen - shift + keep);
      this.ring.set(chunk.subarray(chunk.length - keep), this.ringLen - keep);
    }
    return found;
  }

  /** The recent bytes, for rendering context around a match. Never fed back to the automaton. */
  tail(): Uint8Array {
    return this.ring.subarray(0, this.ringLen);
  }

  /** Absolute offset of the first byte currently in `tail()`. */
  tailStart(): number {
    return this.absolute - this.ringLen;
  }
}

/** Convenience for tests and small buffers: scan a whole buffer in one go. */
export function scanAll(a: CompiledAutomaton, buf: Uint8Array): AcMatch[] {
  return new Scanner(a).push(buf);
}
